import { apiRequest, apiFetch, ApiError } from './core/client';
import { assertRealMailboxTarget } from '@/constants/mailbox';
import {
  EmailSchema,
  parseWithSchema,
  parseArrayWithSchema,
  type Email,
} from './schemas';

export { ApiError };
export type { Email };

// ============================================================================
// Fetch Options
// ============================================================================

export interface FetchEmailsOptions {
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
  mailboxPath?: string;
  filters?: Record<string, string | number | boolean | undefined>;
}

export interface UnifiedSource {
  accountId: string;
  folderId: string;
}

export interface FetchEmailsUnifiedOptions {
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}

// ============================================================================
// Email Fetching
// ============================================================================

export async function fetchEmails(
  accountId: string,
  folderId: string,
  options: FetchEmailsOptions = {}
): Promise<Email[]> {
  const { limit, offset, signal, mailboxPath, filters } = options;
  const data = await apiRequest<unknown[]>({
    path: '/emails',
    accountId,
    query: {
      folder: folderId,
      path: mailboxPath,
      limit,
      offset,
      ...filters,
    },
    signal,
  });

  return parseArrayWithSchema(EmailSchema, data, 'fetchEmails');
}

export async function fetchEmailsUnified(
  sources: UnifiedSource[],
  options: FetchEmailsUnifiedOptions = {}
): Promise<Email[]> {
  const { limit, offset, signal } = options;
  const data = await apiRequest<unknown[]>({
    path: '/emails/unified',
    method: 'POST',
    body: {
      sources,
      limit,
      offset,
    },
    signal,
  });

  if (!Array.isArray(data)) {
    console.warn('[fetchEmailsUnified] Expected array but got:', typeof data);
    return [];
  }

  return parseArrayWithSchema(EmailSchema, data, 'fetchEmailsUnified');
}

export interface EmailMailboxMembership {
  id: string | null;
  role: string | null;
  name: string | null;
}

export interface EmailWithMailboxes {
  email: Email;
  /** Every current mailbox membership — the caller decides "still in inbox?" */
  mailboxes: EmailMailboxMembership[];
}

/**
 * List-item shapes for an explicit set of message ids (max 100), regardless of
 * folder/page. Used to hydrate briefing message refs. Per-account endpoint —
 * unified callers fan out per real account.
 */
export async function fetchEmailsByIds(
  accountId: string,
  ids: string[],
  options: { signal?: AbortSignal } = {}
): Promise<EmailWithMailboxes[]> {
  if (!ids.length) {
    return [];
  }
  const data = await apiRequest<unknown[]>({
    path: '/emails/by-ids',
    method: 'POST',
    accountId,
    body: { ids },
    signal: options.signal,
  });
  if (!Array.isArray(data)) {
    return [];
  }
  return data.map((raw) => {
    const email = parseWithSchema(EmailSchema, raw, 'fetchEmailsByIds');
    const record = raw as Record<string, unknown>;
    const rawMailboxes = Array.isArray(record.mailboxes) ? record.mailboxes : [];
    const mailboxes: EmailMailboxMembership[] = rawMailboxes.map((entry) => {
      const membership = (entry ?? {}) as Record<string, unknown>;
      return {
        id: membership.id != null ? String(membership.id) : null,
        role: typeof membership.role === 'string' ? membership.role : null,
        name: typeof membership.name === 'string' ? membership.name : null,
      };
    });
    return { email, mailboxes };
  });
}

export async function fetchEmail(
  accountId: string,
  emailId: string,
  folderId?: string
): Promise<Email> {
  const data = await apiRequest<unknown>({
    path: `/emails/${encodeURIComponent(emailId)}`,
    accountId,
    query: folderId ? { folder: folderId } : undefined,
  });

  return parseWithSchema(EmailSchema, data, 'fetchEmail');
}

export async function rehydrateEmail(accountId: string, emailId: string): Promise<Email> {
  const data = await apiRequest<unknown>({
    path: `/emails/${encodeURIComponent(emailId)}/rehydrate`,
    method: 'POST',
    accountId,
  });
  return parseWithSchema(EmailSchema, data, 'rehydrateEmail');
}

// ============================================================================
// Email Actions
// ============================================================================

export async function starEmail(
  accountId: string,
  emailId: string,
  starred: boolean,
  folderId: string
): Promise<void> {
  assertRealMailboxTarget(accountId, 'starEmail', 'account');
  assertRealMailboxTarget(folderId, 'starEmail');
  await apiRequest({
    path: `/emails/${encodeURIComponent(emailId)}/star`,
    method: 'PUT',
    accountId,
    body: { starred, folder: folderId },
  });
}

export async function markEmailRead(
  accountId: string,
  emailId: string,
  read: boolean,
  folderId: string
): Promise<void> {
  assertRealMailboxTarget(accountId, 'markEmailRead', 'account');
  assertRealMailboxTarget(folderId, 'markEmailRead');
  await apiRequest({
    path: `/emails/${encodeURIComponent(emailId)}/read`,
    method: 'PUT',
    accountId,
    body: { read, folder: folderId },
  });
}

export async function deleteEmail(
  accountId: string,
  emailId: string,
  folderId: string
): Promise<void> {
  assertRealMailboxTarget(accountId, 'deleteEmail', 'account');
  assertRealMailboxTarget(folderId, 'deleteEmail');
  await apiRequest({
    path: `/emails/${encodeURIComponent(emailId)}`,
    method: 'DELETE',
    accountId,
    query: { folder: folderId },
  });
}

export async function moveEmail(
  accountId: string,
  emailId: string,
  fromFolder: string,
  toFolder: string
): Promise<void> {
  assertRealMailboxTarget(accountId, 'moveEmail', 'account');
  assertRealMailboxTarget(fromFolder, 'moveEmail (fromFolder)');
  assertRealMailboxTarget(toFolder, 'moveEmail (toFolder)');
  await apiRequest({
    path: `/emails/${encodeURIComponent(emailId)}/move`,
    method: 'PUT',
    accountId,
    body: { fromFolder, toFolder },
  });
}

// ============================================================================
// Bulk Email Actions
// ============================================================================

/**
 * The server reports the full acted-on id set — with `expandThreads` it widens
 * the request to whole conversations (co-thread messages in the same folder the
 * client never loaded), and undo bookkeeping needs the honest list.
 */
function extractActedMessageIds(response: unknown): string[] {
  const ids = (response as { messageIds?: unknown })?.messageIds;
  return Array.isArray(ids) ? ids.map(String) : [];
}

export async function bulkMarkEmailsRead(
  accountId: string,
  messageIds: string[],
  read: boolean,
  folderId: string,
  options: { expandThreads?: boolean } = {}
): Promise<{ messageIds: string[] }> {
  assertRealMailboxTarget(accountId, 'bulkMarkEmailsRead', 'account');
  assertRealMailboxTarget(folderId, 'bulkMarkEmailsRead');
  const response = await apiRequest({
    path: '/emails/bulk/read',
    method: 'POST',
    accountId,
    body: { messageIds, read, folderId, expandThreads: options.expandThreads === true },
  });
  return { messageIds: extractActedMessageIds(response) };
}

export async function bulkMoveEmails(
  accountId: string,
  messageIds: string[],
  fromFolder: string,
  toFolder: string,
  options: { expandThreads?: boolean } = {}
): Promise<{ messageIds: string[] }> {
  assertRealMailboxTarget(accountId, 'bulkMoveEmails', 'account');
  assertRealMailboxTarget(fromFolder, 'bulkMoveEmails (fromFolder)');
  assertRealMailboxTarget(toFolder, 'bulkMoveEmails (toFolder)');
  const response = await apiRequest({
    path: '/emails/bulk/move',
    method: 'POST',
    accountId,
    body: { messageIds, fromFolder, toFolder, expandThreads: options.expandThreads === true },
  });
  return { messageIds: extractActedMessageIds(response) };
}

export async function bulkDeleteEmails(
  accountId: string,
  messageIds: string[],
  folderId: string,
  options: { expandThreads?: boolean } = {}
): Promise<{ messageIds: string[] }> {
  assertRealMailboxTarget(accountId, 'bulkDeleteEmails', 'account');
  assertRealMailboxTarget(folderId, 'bulkDeleteEmails');
  const response = await apiRequest({
    path: '/emails/bulk/delete',
    method: 'POST',
    accountId,
    body: { messageIds, folderId, expandThreads: options.expandThreads === true },
  });
  return { messageIds: extractActedMessageIds(response) };
}

// ============================================================================
// Send Email
// ============================================================================

export interface SendEmailPayload {
  accountId: string;
  to: string;
  cc?: string;
  bcc?: string;
  subject: string;
  html: string;
  text?: string;
  attachments?: File[];
  fromAccountId?: string;
  replyToMessageId?: string;
  threadId?: string;
  inReplyTo?: string;
  references?: string;
  idempotencyKey?: string;
  scheduledAt?: string;
  retainedAttachmentIds?: string[];
  draftId?: string;
}

export interface SendEmailResponse {
  success: boolean;
  messageId: string | null;
  threadId: string | null;
}

export async function sendEmail(payload: SendEmailPayload): Promise<SendEmailResponse> {
  const { accountId, attachments, retainedAttachmentIds, ...rest } = payload;
  if (attachments && attachments.length > 0) {
    const formData = new FormData();
    Object.entries(rest).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }
      formData.append(key, String(value));
    });
    if (retainedAttachmentIds && retainedAttachmentIds.length > 0) {
      formData.append('retainedAttachmentIds', JSON.stringify(retainedAttachmentIds));
    }
    attachments.forEach((file) => {
      formData.append('attachments', file);
    });

    return apiRequest<SendEmailResponse>({
      path: '/emails/send',
      method: 'POST',
      accountId,
      formData,
    });
  }

  return apiRequest<SendEmailResponse>({
    path: '/emails/send',
    method: 'POST',
    accountId,
    body: {
      ...rest,
      retainedAttachmentIds: retainedAttachmentIds?.length ? retainedAttachmentIds : undefined,
    },
  });
}

// ============================================================================
// Attachment Download
// ============================================================================

export interface AttachmentDownloadResult {
  blob: Blob;
  /** Filename from the response Content-Disposition header. The server
   * re-resolves attachment metadata from the mail store on first fetch, so
   * this is more accurate than the filename cached in the email store. */
  filename: string | null;
}

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) {
    return null;
  }
  const extended = header.match(/filename\*=utf-8''([^;]+)/i);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim()) || null;
    } catch {
      // fall through to the plain filename parameter
    }
  }
  const quoted = header.match(/filename="([^"]*)"/i);
  if (quoted) {
    return quoted[1].trim() || null;
  }
  const bare = header.match(/filename=([^;]+)/i);
  return bare ? bare[1].trim() || null : null;
}

export async function downloadAttachment(
  accountId: string,
  emailId: string,
  attachmentId: string,
  folderId?: string | null,
  options: { preview?: boolean } = {}
): Promise<AttachmentDownloadResult> {
  const query: Record<string, string> = {};
  if (folderId) {
    query.folder = folderId;
  }
  if (options.preview) {
    query.preview = 'true';
  }
  const response = await apiFetch({
    path: `/emails/${encodeURIComponent(emailId)}/attachments/${encodeURIComponent(attachmentId)}`,
    accountId,
    query,
  });

  if (!response.ok) {
    let errorData: unknown = null;
    try {
      errorData = await response.json();
    } catch {
      errorData = await response.text();
    }
    throw new ApiError(response, errorData);
  }

  const filename = parseContentDispositionFilename(response.headers.get('content-disposition'));
  return { blob: await response.blob(), filename };
}

export interface PrepareAttachmentDownloadResponse {
  status: 'queued' | 'ready';
  attachmentId: string;
  emailId: string;
}

export async function prepareAttachmentDownload(
  accountId: string,
  emailId: string,
  attachmentId: string
): Promise<PrepareAttachmentDownloadResponse> {
  const data = await apiRequest<unknown>({
    path: `/emails/${encodeURIComponent(emailId)}/attachments/${encodeURIComponent(attachmentId)}/prepare`,
    method: 'POST',
    accountId,
  });

  const record = data as Record<string, unknown>;
  const statusValue = typeof record.status === 'string' ? record.status : 'queued';

  return {
    status: statusValue === 'ready' ? 'ready' : 'queued',
    attachmentId: typeof record.attachmentId === 'string' ? record.attachmentId : attachmentId,
    emailId: typeof record.emailId === 'string' ? record.emailId : emailId,
  };
}

const ATTACHMENT_PREPARE_MAX_ATTEMPTS = 4;
const ATTACHMENT_PREPARE_BASE_DELAY = 200;

/**
 * Ask the server to make an attachment's bytes available locally (Gmail/IMAP
 * attachments may not be cached yet) and poll with backoff until it reports
 * `ready`. Call before {@link downloadAttachment}.
 */
export async function prepareAttachmentUntilReady(
  accountId: string,
  emailId: string,
  attachmentId: string
): Promise<void> {
  let delay = ATTACHMENT_PREPARE_BASE_DELAY;
  for (let attempt = 0; attempt < ATTACHMENT_PREPARE_MAX_ATTEMPTS; attempt += 1) {
    const response = await prepareAttachmentDownload(accountId, emailId, attachmentId);
    if (response.status === 'ready') {
      return;
    }
    await new Promise<void>((resolve) => setTimeout(resolve, delay));
    delay = Math.min(delay * 2, 2000);
  }
  throw new Error('Attachment is still preparing');
}

// ============================================================================
// Calendar Response
// ============================================================================

export interface CalendarResponseResult {
  calendarEvent: Email['calendarEvent'];
  responseStatus: string | null;
  respondedAt: string | null;
}

export async function respondToCalendarInvitation(
  accountId: string,
  messageId: string,
  response: 'ACCEPTED' | 'DECLINED' | 'TENTATIVE',
  options: { comment?: string } = {}
): Promise<CalendarResponseResult> {
  const body: Record<string, unknown> = { response };
  if (options.comment) {
    body.comment = options.comment;
  }

  const data = await apiRequest<unknown>({
    path: `/messages/${encodeURIComponent(messageId)}/calendar/respond`,
    method: 'POST',
    accountId,
    body,
  });

  const record = data as Record<string, unknown>;
  const calendarEvent = record.calendarEvent as Email['calendarEvent'] ?? null;

  return {
    calendarEvent,
    responseStatus: typeof record.responseStatus === 'string' ? record.responseStatus : null,
    respondedAt: typeof record.respondedAt === 'string' ? record.respondedAt : null,
  };
}
